import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useCreateMenuSection, useMenuSections, useUpdateMenuSection } from "@/hooks/use-menu";
import { useActiveOrganization } from "@/hooks/use-organizations";
import type { UpsertMenuSectionInput } from "@/types/menu";

const createEmptyFormState = (): UpsertMenuSectionInput => ({
  name: "",
  slug: "",
  description: "",
  parentId: null,
  visible: true,
});

export default function SectionFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: activeOrganization, isLoading: isLoadingOrganization } = useActiveOrganization();
  const { data: sectionsData } = useMenuSections(activeOrganization?.id);
  const createSection = useCreateMenuSection();
  const updateSection = useUpdateMenuSection();
  const [formState, setFormState] = useState<UpsertMenuSectionInput>(createEmptyFormState());

  const editingSection = id ? sectionsData?.sections.find((s) => s.id === id) : undefined;

  useEffect(() => {
    if (editingSection) {
      setFormState({
        name: editingSection.name,
        slug: editingSection.slug,
        description: editingSection.description ?? "",
        parentId: editingSection.parentId,
        visible: editingSection.visible,
        displayOrder: editingSection.displayOrder,
      });
    }
  }, [editingSection]);

  const handleChange = <T extends keyof UpsertMenuSectionInput>(
    field: T,
    value: UpsertMenuSectionInput[T],
  ) => {
    setFormState((currentState) => ({
      ...currentState,
      [field]: value,
    }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const payload: UpsertMenuSectionInput = {
      name: formState.name?.trim() ?? "",
      slug: formState.slug?.trim() || undefined,
      description: formState.description?.trim() || null,
      parentId: formState.parentId || null,
      visible: formState.visible ?? true,
    };

    if (!payload.name) {
      toast.error(t("menu.sections.notifications.nameRequired"));
      return;
    }

    if (id) {
      updateSection.mutate(
        { data: payload, id },
        {
          onError: (mutationError) => {
            toast.error(mutationError.message);
          },
          onSuccess: () => {
            toast.success(t("menu.sections.notifications.updated"));
            navigate("/menu/sections");
          },
        },
      );
      return;
    }

    createSection.mutate(payload, {
      onError: (mutationError) => {
        toast.error(mutationError.message);
      },
      onSuccess: () => {
        toast.success(t("menu.sections.notifications.created"));
        navigate("/menu/sections");
      },
    });
  };

  if (isLoadingOrganization) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!activeOrganization) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("menu.sections.title")}</CardTitle>
            <CardDescription>{t("menu.sections.notifications.activeOrgRequired")}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const isSupermarket = activeOrganization.businessType === "supermarket";
  const copy = {
    cancel: t("menu.sections.form.cancel"),
    create: isSupermarket
      ? t("menu.sections.catalog.form.create", { defaultValue: "Crear categoría" })
      : t("menu.sections.form.create"),
    createTitle: isSupermarket
      ? t("menu.sections.catalog.form.createTitle", { defaultValue: "Crear categoría" })
      : t("menu.sections.form.createTitle"),
    description: isSupermarket
      ? t("menu.sections.catalog.form.description", {
          defaultValue: "Usa categorías y subcategorías para organizar el catálogo público.",
        })
      : t("menu.sections.form.description"),
    descriptionPlaceholder: isSupermarket
      ? t("menu.sections.catalog.form.descriptionPlaceholder", {
          defaultValue: "Texto opcional para mostrar en el catálogo público.",
        })
      : t("menu.sections.form.descriptionPlaceholder"),
    editTitle: isSupermarket
      ? t("menu.sections.catalog.form.editTitle", { defaultValue: "Editar categoría" })
      : t("menu.sections.form.editTitle"),
    name: isSupermarket
      ? t("menu.sections.catalog.form.name", { defaultValue: "Nombre de categoría" })
      : t("menu.sections.form.name"),
    namePlaceholder: isSupermarket
      ? t("menu.sections.catalog.form.namePlaceholder", { defaultValue: "Por ejemplo, Almacén" })
      : t("menu.sections.form.namePlaceholder"),
    noParent: isSupermarket
      ? t("menu.sections.catalog.form.noParent", { defaultValue: "Sin categoría padre" })
      : t("menu.sections.form.noParent", { defaultValue: "No parent category" }),
    parent: isSupermarket
      ? t("menu.sections.catalog.form.parent", { defaultValue: "Categoría padre" })
      : t("menu.sections.form.parent", { defaultValue: "Parent category" }),
    parentDescription: isSupermarket
      ? t("menu.sections.catalog.form.parentDescription", {
          defaultValue:
            "Deja vacío para una categoría principal, o elige una categoría padre para crear una subcategoría.",
        })
      : t("menu.sections.form.parentDescription", {
          defaultValue:
            "Leave empty for a top-level category, or choose one parent category for a subcategory.",
        }),
    save: t("menu.sections.form.save"),
  };

  const formTitle = id ? copy.editTitle : copy.createTitle;

  const submitLabel = id ? copy.save : copy.create;

  const parentSections =
    sectionsData?.sections.filter((section) => section.parentId === null && section.id !== id) ??
    [];

  return (
    <div className="flex flex-col gap-8 p-6">
      <div className="space-y-1">
        <h1 className="font-serif font-semibold text-2xl tracking-tight">{formTitle}</h1>
        <p className="text-muted-foreground text-sm">{copy.description}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-serif">{formTitle}</CardTitle>
          <CardDescription>{copy.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-4" onSubmit={handleSubmit}>
            <div className="grid gap-2">
              <Label htmlFor="menu-section-name">{copy.name}</Label>
              <Input
                id="menu-section-name"
                onChange={(event) => handleChange("name", event.target.value)}
                placeholder={copy.namePlaceholder}
                value={formState.name ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="menu-section-slug">{t("menu.sections.form.slug")}</Label>
              <Input
                id="menu-section-slug"
                onChange={(event) => handleChange("slug", event.target.value)}
                placeholder={t("menu.sections.form.slugPlaceholder")}
                value={formState.slug ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="menu-section-description">
                {t("menu.sections.form.longDescription")}
              </Label>
              <Textarea
                id="menu-section-description"
                onChange={(event) => handleChange("description", event.target.value)}
                placeholder={copy.descriptionPlaceholder}
                rows={4}
                value={formState.description ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="menu-section-parent">{copy.parent}</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                id="menu-section-parent"
                onChange={(event) => handleChange("parentId", event.target.value || null)}
                value={formState.parentId ?? ""}
              >
                <option value="">{copy.noParent}</option>
                {parentSections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.name}
                  </option>
                ))}
              </select>
              <p className="text-muted-foreground text-xs">{copy.parentDescription}</p>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-1">
                <p className="font-medium text-sm">{t("menu.sections.form.visible")}</p>
                <p className="text-muted-foreground text-sm">
                  {t("menu.sections.form.visibleDescription")}
                </p>
              </div>
              <Switch
                checked={formState.visible ?? true}
                onCheckedChange={(checked) => handleChange("visible", checked)}
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <Button disabled={createSection.isPending || updateSection.isPending} type="submit">
                {submitLabel}
              </Button>
              <Button onClick={() => navigate("/menu/sections")} type="button" variant="outline">
                {copy.cancel}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
